/**
 * ==========================================================================
 *  TWO SURFACES ON ONE PLANE
 * ==========================================================================
 *  The blockout is axis-aligned boxes pushed up against each other, and
 *  authoring it that way means two of them land on the same plane constantly:
 *  the pitkä pöytä is built into the west end of the palju's fence, the
 *  navetta's walls meet at shared outer corners, every kuusi in a row is cut
 *  off at the same height, and the gravel outside the porstua is laid over the
 *  same square metre of ground as the stone floor of the porch.
 *
 *  Where two drawn surfaces sit on exactly the same plane the depth buffer has
 *  no answer to give. Which of them a pixel gets comes down to rounding, and
 *  the rounding changes as the camera moves - so the surface flickers between
 *  two materials while you run past it. It is the one artefact in the game
 *  that reads as *broken* rather than as coarse.
 *
 *  ------------------------------------------------------------------------
 *   WHY IT IS NOT FIXED IN THE LEVEL
 *  ------------------------------------------------------------------------
 *  Moving one of the two boxes a millimetre would fix any single case, and it
 *  is the wrong fix twice over.
 *
 *  `simulationFingerprint()` hashes every solid's collider, so nudging a box
 *  because it flickers retires every time on the leaderboard - for a rendering
 *  bug, in a level with a couple of hundred of these. And a millimetre is only
 *  enough close up. The depth buffer's resolution is roughly `z²` metres over
 *  a million here: half a millimetre at twenty metres, five at sixty, and the
 *  yard is a hundred and eighty metres across. There is no fixed distance in
 *  metres that is both invisible up close and sufficient at the far fence.
 *
 *  ------------------------------------------------------------------------
 *   WHAT HAPPENS INSTEAD
 *  ------------------------------------------------------------------------
 *  Nothing moves. The surfaces that share a plane are found up front, and the
 *  loser of each tie is drawn with `gl.polygonOffset`, which biases the depth
 *  it writes by a number of *depth buffer units* rather than by a number of
 *  metres. That is the same nudge at every distance - exactly as much as the
 *  rounding it has to beat and no more - so it can only ever decide a tie. It
 *  is far too small to reorder two surfaces that were a centimetre apart on
 *  purpose, which is why being generous about what counts as one plane costs
 *  nothing.
 *
 *  Which of the two loses is decided by size: of two surfaces on one plane the
 *  smaller is the detail and the larger is what it was laid on, so the smaller
 *  is the one you see. The path over the yard, not the yard over the path.
 *  Walls, fences and the tub take precedence over objects built into them;
 *  cladding and trim still belong in front of those surfaces. Finished floors
 *  likewise cover outdoor tracks where they meet on the same plane.
 *
 *  The result is a *level* per drawn thing - 0 for everything not in a tie, and
 *  0, 1, 2... within one. It is a graph colouring: the shapes are the nodes,
 *  sharing a plane is an edge, and two shapes that share a plane may not share
 *  a level. Higher-priority neighbours must also stay in front. Colouring
 *  rather than a level per shape is what keeps the bias down
 *  to the one or two units it actually takes, however long a row of kuusi is.
 *
 *  Geometry only, and rendering-only geometry at that. Nothing here is read by
 *  the simulation, no collider is touched, and F4 still draws what is solid.
 * ==========================================================================
 */

import { RENDER } from '../config/gameplay';
import type { Box, FloorSpec, LevelDefinition, MaterialKey } from '../world/Level';

/** Which world axis a face looks along: 0 = x, 1 = y, 2 = z. */
type Axis = 0 | 1 | 2;

/** A point in the two axes a face does *not* look along. */
type Corner = [number, number];

interface Face {
  axis: Axis;
  /** Where the face sits on that axis. */
  plane: number;
  /** Which way it looks: -1 towards the axis minimum, +1 towards its maximum. */
  facing: -1 | 1;
  /** The face itself, as a convex outline in the other two axes, in order. */
  outline: Corner[];
}

/** One drawn thing, as the faces it can be caught sharing a plane with. */
export interface CoplanarShape {
  faces: Face[];
  /** Higher priorities stay in front of lower-priority coplanar neighbours. */
  priority?: number;
  /**
   * How big it is. Only ever compared with another shape's, to decide which of
   * two surfaces on one plane is the detail. Any measure that grows with the
   * thing will do; this is drawn surface area, because a floor has no volume.
   */
  size: number;
  /** Extent per axis, so that most pairs are rejected without looking further. */
  min: [number, number, number];
  max: [number, number, number];
}

/** Depth levels for one level's drawn geometry, index by index. */
export interface LevelDepthOrder {
  floors: number[];
  solids: number[];
  decor: number[];
}

/** The two axes a face on `axis` spans, in the order its outline uses them. */
function others(axis: Axis): [Axis, Axis] {
  return axis === 0 ? [1, 2] : axis === 1 ? [0, 2] : [0, 1];
}

function shapeOf(faces: Face[], size: number): CoplanarShape {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (const face of faces) {
    min[face.axis] = Math.min(min[face.axis], face.plane);
    max[face.axis] = Math.max(max[face.axis], face.plane);
    const [u, v] = others(face.axis);
    for (const corner of face.outline) {
      min[u] = Math.min(min[u], corner[0]);
      max[u] = Math.max(max[u], corner[0]);
      min[v] = Math.min(min[v], corner[1]);
      max[v] = Math.max(max[v], corner[1]);
    }
  }
  return { faces, size, min, max };
}

/** The six faces of a box, as `World.addBox` draws them. */
export function boxShape(box: Box): CoplanarShape {
  const rect = (u0: number, u1: number, v0: number, v1: number): Corner[] => [
    [u0, v0],
    [u1, v0],
    [u1, v1],
    [u0, v1],
  ];
  const alongX = rect(box.minY, box.maxY, box.minZ, box.maxZ);
  const alongY = rect(box.minX, box.maxX, box.minZ, box.maxZ);
  const alongZ = rect(box.minX, box.maxX, box.minY, box.maxY);
  const width = box.maxX - box.minX;
  const height = box.maxY - box.minY;
  const depth = box.maxZ - box.minZ;

  return shapeOf(
    [
      { axis: 0, plane: box.minX, facing: -1, outline: alongX },
      { axis: 0, plane: box.maxX, facing: 1, outline: alongX },
      { axis: 1, plane: box.minY, facing: -1, outline: alongY },
      { axis: 1, plane: box.maxY, facing: 1, outline: alongY },
      { axis: 2, plane: box.minZ, facing: -1, outline: alongZ },
      { axis: 2, plane: box.maxZ, facing: 1, outline: alongZ },
    ],
    2 * (width * height + height * depth + width * depth),
  );
}

/**
 * A ground patch: one quad, facing up, possibly turned.
 *
 * The corners are worked out the way `World.buildFloors` lays the plane down -
 * flat first, so the quad's own x runs along world +x and its own y along world
 * -z, and `rotationY` turns it in that plane afterwards.
 */
export function floorShape(spec: FloorSpec): CoplanarShape {
  const angle = spec.rotationY ?? 0;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const [width, depth] = spec.size;

  const outline = ([
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ] as Corner[]).map(([su, sv]): Corner => {
    const u = (su * width) / 2;
    const v = (sv * depth) / 2;
    return [spec.center[0] + u * cos - v * sin, spec.center[1] - (u * sin + v * cos)];
  });

  return shapeOf([{ axis: 1, plane: spec.y, facing: 1, outline }], width * depth);
}

/** How far two convex outlines overlap at their tightest, in metres. */
function penetration(a: Corner[], b: Corner[]): number {
  let least = Infinity;
  for (const [one, other] of [
    [a, b],
    [b, a],
  ] as [Corner[], Corner[]][]) {
    for (let at = 0; at < one.length; at++) {
      const [x1, y1] = one[at];
      const [x2, y2] = one[(at + 1) % one.length];
      // The edge's normal, normalised - the answer is wanted in metres.
      const length = Math.hypot(y2 - y1, x1 - x2);
      if (length < 1e-9) continue;
      const axis: Corner = [(y2 - y1) / length, (x1 - x2) / length];
      const first = span(one, axis);
      const second = span(other, axis);
      least = Math.min(least, Math.min(first[1], second[1]) - Math.max(first[0], second[0]));
      if (least <= 0) return least;
    }
  }
  return least;
}

function span(corners: Corner[], axis: Corner): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  for (const [x, y] of corners) {
    const at = x * axis[0] + y * axis[1];
    min = Math.min(min, at);
    max = Math.max(max, at);
  }
  return [min, max];
}

/**
 * Whether two shapes have a pair of faces that would fight.
 *
 * Both conditions matter. The faces have to look the *same* way: two boxes
 * standing back to back share a plane too, but one of those faces is turned
 * away from every camera that can see the other, and back-face culling has
 * already settled it. And they have to genuinely cover each other rather than
 * merely touch along an edge.
 */
export function sharesAPlane(a: CoplanarShape, b: CoplanarShape): boolean {
  const { samePlane, overlap } = RENDER.coplanar;
  for (let axis = 0; axis < 3; axis++) {
    if (a.min[axis] - b.max[axis] > samePlane) return false;
    if (b.min[axis] - a.max[axis] > samePlane) return false;
  }
  for (const one of a.faces) {
    for (const other of b.faces) {
      if (one.axis !== other.axis || one.facing !== other.facing) continue;
      if (Math.abs(one.plane - other.plane) > samePlane) continue;
      if (penetration(one.outline, other.outline) > overlap) return true;
    }
  }
  return false;
}

/**
 * A depth level per shape: 0 draws in front, and every shape sharing a plane
 * with another gets a level that one does not have.
 */
export function depthOrder(shapes: readonly CoplanarShape[]): number[] {
  const ties: number[][] = shapes.map(() => []);
  for (let a = 0; a < shapes.length; a++) {
    for (let b = a + 1; b < shapes.length; b++) {
      if (!sharesAPlane(shapes[a], shapes[b])) continue;
      ties[a].push(b);
      ties[b].push(a);
    }
  }

  // Priority first, then smallest, so detail keeps level 0 and its backing is
  // the one pushed back. Equal sizes fall back to position rather than to
  // whatever order the sort happened to leave them in.
  const order = shapes.map((_, at) => at);
  order.sort((a, b) =>
    (shapes[b].priority ?? 0) - (shapes[a].priority ?? 0) ||
    shapes[a].size - shapes[b].size || a - b,
  );

  const level = new Array<number>(shapes.length).fill(-1);
  for (const at of order) {
    const taken = new Set<number>();
    let free = 0;
    for (const other of ties[at]) {
      if (level[other] >= 0) taken.add(level[other]);
      if ((shapes[other].priority ?? 0) > (shapes[at].priority ?? 0)) {
        // A higher-priority neighbour may itself have lost level 0 to a peer.
        // Reusing that free slot here would draw this object through it.
        free = Math.max(free, level[other] + 1);
      }
    }
    while (taken.has(free)) free++;
    level[at] = free;
  }
  return level;
}

/** Structural skins hide embedded objects; authored cladding covers the skin. */
function surfacePriority(material: MaterialKey): number {
  switch (material) {
    case 'faluRed':
    case 'porcelain':
      return 2;
    case 'pirttiWall':
    case 'barnWall':
    case 'fence':
    case 'tub':
      return 1;
    default:
      return 0;
  }
}

/**
 * The same, for a whole level, over everything `World` draws as a plain box or
 * a ground quad.
 *
 * The solids with a `shape` are left out at level 0: a car, a camper, the
 * bemari's cable and the veräjä's leaf are assembled by builders of their own
 * out of parts this does not see, and an `invisible` solid is not drawn at all.
 * If one of those ever starts flickering it wants its own answer, not a level
 * here that its parts would not inherit.
 */
export function levelDepthOrder(level: LevelDefinition): LevelDepthOrder {
  const drawnAsBox = level.solids.map((spec) => spec.shape === undefined);

  const shapes: CoplanarShape[] = [
    ...level.floors.map((spec) => ({
      ...floorShape(spec),
      priority: ['stone', 'wood', 'pirttiFloor', 'lanFloor'].includes(spec.material) ? 1 : 0,
    })),
    ...level.solids.filter((_, at) => drawnAsBox[at]).map((spec) => ({
      ...boxShape(spec.box),
      priority: surfacePriority(spec.material),
    })),
    ...level.decor.map((spec) => ({
      ...boxShape(spec.box),
      priority: surfacePriority(spec.material),
    })),
  ];
  const levels = depthOrder(shapes);

  let next = level.floors.length;
  const floors = levels.slice(0, next);
  const solids = drawnAsBox.map((isBox) => (isBox ? levels[next++] : 0));
  const decor = levels.slice(next);

  return { floors, solids, decor };
}
